import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getEvents,
  subscribeToLocation,
  deleteEvent,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  Calendar,
  Clock,
  MapPin,
  Users,
  User,
  ExternalLink,
  Edit3,
  Trash2,
  Eye,
  UserPlus,
  Music,
  GraduationCap,
  Briefcase,
  Coffee,
  Heart,
  Grid3X3,
  List,
  Table,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedPageLayout,
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";

type EventType =
  | "workshop"
  | "meeting"
  | "cultural"
  | "business"
  | "social"
  | "educational"
  | "all";

const EVENT_TYPES = [
  { value: "all", label: "All Types", icon: Filter },
  { value: "workshop", label: "Workshop", icon: GraduationCap },
  { value: "meeting", label: "Meeting", icon: Users },
  { value: "cultural", label: "Cultural", icon: Music },
  { value: "business", label: "Business", icon: Briefcase },
  { value: "social", label: "Social", icon: Coffee },
  { value: "educational", label: "Educational", icon: GraduationCap },
];

export default function EventsPage() {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const { selectedLocation } = useLocationContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<EventType>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "title" | "date">(
    "date",
  );

  // Use integrated responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("cards");

  useRequireSectionVisible("showEvent");

  const {
    data: eventsData = [],
    isLoading,
    error,
  } = useQuery(getEvents, {
    locationId: selectedLocation?.id || "",
  });

  const filteredEvents = useMemo(() => {
    let result = [...eventsData];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (event) =>
          event.title.toLowerCase().includes(term) ||
          event.description.toLowerCase().includes(term),
      );
    }

    if (selectedType !== "all") {
      result = result.filter((event) => event.type === selectedType);
    }

    result.sort((a, b) => {
      if (sortBy === "date")
        return (
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        );
      if (sortBy === "newest")
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      if (sortBy === "oldest")
        return (
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      if (sortBy === "title") return a.title.localeCompare(b.title);
      return 0;
    });

    return result;
  }, [eventsData, searchTerm, selectedType, sortBy]);

  const getTypeIcon = (type: string) => {
    const found = EVENT_TYPES.find((t) => t.value === type);
    const Icon = found?.icon || Calendar;
    return <Icon className="w-4 h-4" />;
  };

  const getTypeColor = (type: string) => {
    const colors = {
      workshop: "bg-purple-100 text-purple-800",
      meeting: "bg-blue-100 text-blue-800",
      cultural: "bg-orange-100 text-orange-800",
      business: "bg-slate-100 text-slate-800",
      social: "bg-pink-100 text-pink-800",
      educational: "bg-indigo-100 text-indigo-800",
    };
    return colors[type as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  const headerContent = (
    <div className="px-4 py-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
            {selectedLocation?.name || "Location"} • {filteredEvents.length} upcoming events
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EnhancedActionButton
            onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            icon={Filter}
            label=""
            variant={isFilterExpanded ? "primary" : "secondary"}
            className="hidden lg:flex"
          />

          {/* Mobile Filter Button */}
          <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
            <EnhancedActionButton
              onClick={() => setIsFilterExpanded(true)}
              icon={Filter}
              label=""
              variant={isFilterExpanded ? "primary" : "secondary"}
              className="lg:hidden"
            />
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value as EventType)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {EVENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="date">Event Date</option>
                    <option value="newest">Recently Added</option>
                    <option value="title">Title A-Z</option>
                  </select>
                </div>

                <Button 
                  className="w-full" 
                  onClick={() => setIsFilterExpanded(false)}
                >
                  Apply Filters
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          {user?.isAdmin && (
            <EnhancedActionButton
              onClick={() => {
                if (selectedLocation?.locationCode) {
                  navigate(
                    `/locations/${selectedLocation.locationCode}/events/add`,
                  );
                } else {
                  navigate("/events/add");
                }
              }}
              icon={Plus}
              label={<span className="hidden sm:inline text-white">Add Event</span>}
            />
          )}
        </div>
      </div>
    </div>
  );

  const filtersContent = (
    <div 
      className={cn(
        "bg-sidebar-hover border-t border-border transition-all duration-300 overflow-hidden hidden lg:block",
        isFilterExpanded ? "max-h-[500px]" : "max-h-0 border-none"
      )}
    >
      <div className="px-6 py-3 space-y-3">
        {/* Column Headers - Only for table view */}
        {viewMode === "table" && (
          <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
            <div className="col-span-3 min-w-0 truncate">Event Name</div>
            <div className="col-span-1 min-w-0 truncate">Type</div>
            <div className="col-span-2 min-w-0 truncate">Location/Venue</div>
            <div className="col-span-2 min-w-0 truncate">Date & Time</div>
            <div className="col-span-1 min-w-0 truncate">Capacity</div>
            <div className="col-span-2 min-w-0 truncate">Status</div>
            {user?.isAdmin && <div className="col-span-1 text-right">Actions</div>}
          </div>
        )}
        
        {/* Column Filters */}
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="col-span-1">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as EventType)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {EVENT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            {/* Location filter placeholder */}
          </div>
          <div className="col-span-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="date">Soonest</option>
              <option value="newest">Recent</option>
              <option value="title">A-Z</option>
            </select>
          </div>
          
          <div className="col-span-1">
            {/* Attendees filter holder */}
          </div>

          <div className="col-span-2">
            {/* Status filter holder */}
          </div>

          {user?.isAdmin && <div className="col-span-1" />}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <EnhancedPageLayout
        header={headerContent}
        filters={filtersContent}
      >
        <div className="w-full">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading events...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Calendar className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Error Loading Events
                </h3>
                <p className="text-gray-600">Please try again later.</p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && filteredEvents.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Events Found
                </h3>
                <p className="text-gray-600">
                  {searchTerm || selectedType !== "all"
                    ? "Try adjusting your search or filters to find what you're looking for."
                    : "Be the first to create an event at this location!"}
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          {!isLoading && !error && filteredEvents.length > 0 && (
            <>
              {/* Cards View */}
              {viewMode === "cards" && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredEvents.map((event) => (
                      <div
                        key={event.id}
                        className={enhancedPageLayoutStyles.card.container}
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        <div className={enhancedPageLayoutStyles.card.content}>
                          <div className={enhancedPageLayoutStyles.card.header}>
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  "px-2 py-0.5 rounded text-xs font-medium",
                                  getTypeColor(event.type),
                                )}
                              >
                                {event.type}
                              </span>
                            </div>
                            <EnhancedIconButton
                              onClick={(e) => {
                                e?.stopPropagation();
                                navigate(`/events/${event.id}`);
                              }}
                              icon={Eye}
                              title="View Details"
                            />
                          </div>
                          <h3 className={enhancedPageLayoutStyles.card.title}>
                            {event.title}
                          </h3>
                          <div className="flex flex-col gap-2 mb-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4" />
                              <span>
                                {new Date(event.startDate).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="w-4 h-4" />
                              <span>
                                {new Date(event.startDate).toLocaleTimeString(
                                  [],
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600 line-clamp-1">
                              <MapPin className="w-4 h-4" />
                              <span>{event.venue || "TBA"}</span>
                            </div>
                          </div>
                          <div className={enhancedPageLayoutStyles.card.footer}>
                            <div className={enhancedPageLayoutStyles.card.meta}>
                              <Users className="w-4 h-4" />
                              <span>
                                {event.attendeesCount || 0} attending
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {user?.isAdmin && (
                                <EnhancedIconButton
                                  onClick={(e) => {
                                    e?.stopPropagation();
                                    navigate(`/events/${event.id}/edit`);
                                  }}
                                  icon={Edit3}
                                  title="Edit"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* List View */}
              {viewMode === "list" && (
                <div className="p-6 space-y-4">
                  {filteredEvents.map((event) => (
                    <div
                      key={event.id}
                      className={enhancedPageLayoutStyles.list.item}
                      onClick={() => navigate(`/events/${event.id}`)}
                    >
                      <div className={enhancedPageLayoutStyles.list.content}>
                        <div className={enhancedPageLayoutStyles.list.left}>
                          <div className={cn(
                            "w-12 h-12 rounded-lg flex items-center justify-center",
                            getTypeColor(event.type)
                          )}>
                            {getTypeIcon(event.type)}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {event.title}
                            </h3>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(event.startDate).toLocaleDateString()}
                              </div>
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.venue || "TBA"}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className={enhancedPageLayoutStyles.list.right}>
                          <div className="text-right mr-4">
                            <span className="block text-sm font-bold text-gray-900">
                              {event.attendeesCount || 0}
                            </span>
                            <span className="text-[10px] text-gray-500 uppercase">
                              Attending
                            </span>
                          </div>
                          <EnhancedIconButton
                            onClick={(e) => {
                              e?.stopPropagation();
                              navigate(`/events/${event.id}`);
                            }}
                            icon={Eye}
                            title="View"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Table View */}
              {viewMode === "table" && (
                <div className="w-full">
                  <div className={enhancedPageLayoutStyles.table.container}>
                    <div className={enhancedPageLayoutStyles.table.wrapper}>
                      <table className={enhancedPageLayoutStyles.table.table}>
                        <tbody
                          className={enhancedPageLayoutStyles.table.tbody}
                        >
                          {filteredEvents.map((event) => (
                            <tr
                              key={event.id}
                              className="group hover:bg-sidebar-hover transition-colors cursor-pointer"
                              onClick={() => navigate(`/events/${event.id}`)}
                            >
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-3",
                                )}
                              >
                                <div className="px-6 py-4 flex items-center gap-3">
                                  <div className={cn(
                                    "w-8 h-8 rounded flex items-center justify-center flex-shrink-0",
                                    getTypeColor(event.type)
                                  )}>
                                    {getTypeIcon(event.type)}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="font-medium text-gray-900 truncate">
                                      {event.title}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {event.description}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-1",
                                )}
                              >
                                <div className="px-6 py-4">
                                  <span
                                    className={cn(
                                      "px-2 py-1 rounded-full text-[10px] font-medium uppercase",
                                      getTypeColor(event.type),
                                    )}
                                  >
                                    {event.type}
                                  </span>
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-2",
                                )}
                              >
                                <div className="px-6 py-4 text-sm text-gray-600 truncate">
                                  {event.venue || "TBA"}
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-2",
                                )}
                              >
                                <div className="px-6 py-4 text-sm text-gray-600">
                                  <div>
                                    {new Date(
                                      event.startDate,
                                    ).toLocaleDateString()}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {new Date(
                                      event.startDate,
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </div>
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-1",
                                )}
                              >
                                <div className="px-6 py-4 text-sm text-gray-600">
                                  {event.attendeesCount || 0}
                                </div>
                              </td>
                              <td
                                className={cn(
                                  enhancedPageLayoutStyles.table.td,
                                  "col-span-2",
                                )}
                              >
                                <div className="px-6 py-4">
                                  <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-[10px] font-bold">
                                    UPCOMING
                                  </span>
                                </div>
                              </td>
                              {user?.isAdmin && (
                                <td
                                  className={cn(
                                    enhancedPageLayoutStyles.table.td,
                                    "col-span-1",
                                  )}
                                >
                                  <div className="px-6 py-4 flex justify-end gap-2">
                                    <EnhancedIconButton
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        navigate(`/events/${event.id}/edit`);
                                      }}
                                      icon={Edit3}
                                      title="Edit"
                                    />
                                    <EnhancedIconButton
                                      onClick={(e) => {
                                        e?.stopPropagation();
                                        if (
                                          confirm(
                                            "Are you sure you want to delete this event?",
                                          )
                                        ) {
                                          deleteEvent({ eventId: event.id });
                                          // Note: In real app we'd refetch
                                        }
                                      }}
                                      icon={Trash2}
                                      title="Delete"
                                      variant="danger"
                                    />
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Mobile version of table items */}
                  <div className="lg:hidden p-4 space-y-4">
                    {filteredEvents.map((event) => (
                      <div
                        key={event.id}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                        onClick={() => navigate(`/events/${event.id}`)}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-bold text-gray-900">
                            {event.title}
                          </h3>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(event.startDate).toLocaleDateString()}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Clock className="w-3.5 h-3.5" />
                            {new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <Button size="sm" variant="outline">View</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </EnhancedPageLayout>
    </DashboardLayout>
  );
}
